import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react'
import { getAssignedOrders } from './service/ordersApi'
import { getCompanyLocaleInfo } from './companyUtils'
import { isAfterCutoff } from './timezoneUtils'
import AddressPointIcon from './components/AddressPointIcon'

// Intervalo de recarga de órdenes asignadas en segundos (30 segundos)
const ApiLoadingInterval = 30

export default function AssignedBuildings({ onSelect, lang = 'en' }) {
   // Estado para mostrar fecha/hora actual en la zona horaria de la empresa
   const [currentInfo, setCurrentInfo] = useState({ fecha: '', hora: '', timezone: '' })

   const [orders, setOrders] = useState([])
   const [loading, setLoading] = useState(true)
   const [error, setError] = useState('')

   const abortRef = useRef(null)
   const fetchingRef = useRef(false)
   const hasLoadedRef = useRef(false)
   const previousDataRef = useRef(null)
   const renderTimingRef = useRef(null)

   useLayoutEffect(() => {
      const timing = renderTimingRef.current
      if (!timing) return undefined

      const committedAt = performance.now()
      const frameId = requestAnimationFrame(() => {
         const paintedAt = performance.now()
         console.table({
            'Backend + red de órdenes (ms)': Math.round(timing.ordersReceivedAt - timing.requestStartedAt),
            'Espera total de API (ms)': Math.round(timing.processingStartedAt - timing.requestStartedAt),
            'Procesamiento en cliente (ms)': Math.round(timing.stateUpdatedAt - timing.processingStartedAt),
            'Respuesta de órdenes a commit React (ms)': Math.round(committedAt - timing.ordersReceivedAt),
            'Respuesta de órdenes a pintado (ms)': Math.round(paintedAt - timing.ordersReceivedAt),
            'Órdenes renderizadas': timing.orderCount,
         })
         renderTimingRef.current = null
      })

      return () => cancelAnimationFrame(frameId)
   }, [orders])

   const fetchOrders = useCallback(async () => {
      if (fetchingRef.current) return
      fetchingRef.current = true

      if (abortRef.current) {
         abortRef.current.abort()
      }

      const controller = new AbortController()
      abortRef.current = controller

      setError('')
      if (!hasLoadedRef.current) setLoading(true)

      try {
         const cachedFallback = await getAssignedOrders(controller.signal, { cacheOnly: true })
         if (Array.isArray(cachedFallback) && cachedFallback.length > 0 && !hasLoadedRef.current) {
            setOrders(cachedFallback)
            hasLoadedRef.current = true
            setLoading(false)
            fetchingRef.current = false
            return
         }

         const requestStartedAt = performance.now()
         let ordersReceivedAt = requestStartedAt

         const localeInfo = getCompanyLocaleInfo()
         const data = await getAssignedOrders(controller.signal).then(result => {
            ordersReceivedAt = performance.now()
            return result
         })
         const processingStartedAt = performance.now()

         let timezone = 'America/Los_Angeles'
         let cutoffHour = 19
         let cutoffMinute = 0
         // Usar taskPublishTime de settings como fuente única de la hora de corte
         if (localeInfo) {
            timezone = localeInfo.zonaHoraria || timezone
            // Mostrar fecha y hora actual en la zona horaria de la empresa
            const now = new Date()
            const local = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
            const fecha = `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`
            const hora = `${String(local.getHours()).padStart(2, '0')}:${String(local.getMinutes()).padStart(2, '0')}`
            setCurrentInfo({ fecha, hora, timezone })
            // Buscar taskPublishTime en settings (localStorage)
            let taskPublishTime = null
            const settingsStr = window.localStorage.getItem('company_settings')
            if (settingsStr) {
               try {
                  const settings = JSON.parse(settingsStr)
                  if (settings.taskPublishTime) taskPublishTime = settings.taskPublishTime
               } catch {}
            }
            // Si no está en localStorage, buscar en localeInfo
            if (!taskPublishTime && localeInfo.taskPublishTime) {
               taskPublishTime = localeInfo.taskPublishTime
            }
            // Si existe taskPublishTime, usarlo
            if (taskPublishTime && typeof taskPublishTime === 'string') {
               const [h, m] = taskPublishTime.split(':').map(Number)
               if (!isNaN(h)) cutoffHour = h
               if (!isNaN(m)) cutoffMinute = m
            }
         }

         const normalized = Array.isArray(data) ? [...data] : []

         // Filtrar órdenes según la hora de corte y zona horaria
         let filtered = normalized
         if (normalized.length > 0 && normalized[0].serviceDate) {
            // Determinar si ya pasó la hora de corte
            const afterCutoff = isAfterCutoff(timezone, cutoffHour, cutoffMinute)
            // Obtener la fecha de hoy y mañana en la zona horaria
            const now = new Date()
            const today = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
            const yyyy = today.getFullYear()
            const mm = String(today.getMonth() + 1).padStart(2, '0')
            const dd = String(today.getDate()).padStart(2, '0')
            const todayStr = `${yyyy}-${mm}-${dd}`
            // Día siguiente
            const tomorrow = new Date(today)
            tomorrow.setDate(today.getDate() + 1)
            const yyyy2 = tomorrow.getFullYear()
            const mm2 = String(tomorrow.getMonth() + 1).padStart(2, '0')
            const dd2 = String(tomorrow.getDate()).padStart(2, '0')
            const tomorrowStr = `${yyyy2}-${mm2}-${dd2}`

            filtered = normalized.filter(order => {
               // serviceDate puede venir como string o Date
               let dateStr = order.serviceDate
               // Normalizar a yyyy-mm-dd para comparar
               if (typeof dateStr === 'string' && dateStr.length >= 10) {
                  // yyyy-mm-dd o yyyy/mm/dd
                  if (/^\d{4}[-/]\d{2}[-/]\d{2}$/.test(dateStr.slice(0, 10))) {
                     dateStr = dateStr.slice(0, 10).replace(/\//g, '-')
                  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr.slice(0, 10))) {
                     // dd/MM/yyyy
                     const [dd, mm, yyyy] = dateStr.slice(0, 10).split('/')
                     dateStr = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
                  }
               } else if (order.serviceDate instanceof Date) {
                  const d = order.serviceDate
                  dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
               }
               // Comparar solo con formato yyyy-mm-dd
               if (afterCutoff) {
                  const match = dateStr === tomorrowStr
                  return match
               } else {
                  const match = dateStr === todayStr
                  return match
               }
            })
         }

         // 🔥 Comparación profunda simple para evitar re-render innecesario
         const newString = JSON.stringify(filtered)
         const oldString = previousDataRef.current

         if (newString !== oldString) {
            renderTimingRef.current = {
               requestStartedAt,
               ordersReceivedAt,
               processingStartedAt,
               stateUpdatedAt: performance.now(),
               orderCount: filtered.length,
            }
            setOrders(filtered)
            previousDataRef.current = newString
         } else {
            console.table({
               'Backend + red de órdenes (ms)': Math.round(ordersReceivedAt - requestStartedAt),
               'Espera total de API (ms)': Math.round(processingStartedAt - requestStartedAt),
               'Procesamiento sin cambios (ms)': Math.round(performance.now() - processingStartedAt),
               'Órdenes recibidas': filtered.length,
            })
         }

         hasLoadedRef.current = true

      } catch (e) {
         if (e.name !== 'AbortError') {
            setError(e.message)
         }
      } finally {
         setLoading(false)
         fetchingRef.current = false
      }
   }, [])

   /* =========================
      INITIAL LOAD + SMART POLLING
   ========================== */
   useEffect(() => {
      fetchOrders()

      const interval = setInterval(() => {
         if (document.visibilityState === 'visible') {
            fetchOrders()
         }
      }, (ApiLoadingInterval * 1000))

      return () => {
         clearInterval(interval)
         if (abortRef.current) abortRef.current.abort()
      }

   }, [fetchOrders])

   /* =========================
      RENDER STATES
   ========================== */
   if (loading) {
      return (
         <div style={{ textAlign: 'center', margin: '32px 0' }}>
            {lang === 'en'
               ? 'Loading assigned buildings...'
               : 'Cargando edificios asignados...'}
         </div>
      )
   }

   if (error) {
      return (
         <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <div style={{ color: 'red', marginBottom: 12 }}>
               {lang === 'en'
                  ? 'Error loading buildings:'
                  : 'Error al cargar edificios:'} {error}
            </div>

            <button onClick={fetchOrders}>
               {lang === 'en' ? 'Retry' : 'Reintentar'}
            </button>
         </div>
      )
   }

   if (!orders.length) {
      return (
         <div style={{ textAlign: 'center', margin: '32px 0' }}>
            <h2 style={{ fontWeight: 600, marginBottom: 8 }}>
               {lang === 'en' ? `Assigned buildings` : `Edificios asignados`}
            </h2>
            <div>{currentInfo.fecha} - {currentInfo.hora}</div>
            <div>{currentInfo.timezone}</div>
            <div style={{ padding: '8px', margin: '16px', background: '#fffaca', borderRadius: 8, fontSize: '1.25em', color: '#333' }}> 
               {lang === 'en' ? `No pending tasks` : `Sin tareas pendientes`}
            </div>
         </div>
      )
   }

   /* =========================
      SORT
   ========================== */
   const sortedOrders = [...orders].sort((a, b) => {
      const pa = typeof a.priority === 'number' ? a.priority : 9999
      const pb = typeof b.priority === 'number' ? b.priority : 9999
      if (pa !== pb) return pa - pb
      const na = parseInt(a.orderNumber || '', 10) || 0
      const nb = parseInt(b.orderNumber || '', 10) || 0
      return na - nb
   })

   /* =========================
      RENDER LIST
   ========================== */
   return (
      <div className='stop-content'>
         <h2 className='h2'>{lang === 'en' ? `Assigned buildings`:`Edificios asignados`}</h2>
         <div>
            <div>{currentInfo.fecha} {currentInfo.hora}</div>
            <div>{currentInfo.timezone}</div>
         </div>
         <ul style={{ listStyle: 'none', padding: 0 }}>
            {sortedOrders.map(order => (
               <li key={order.id} style={{ marginBottom: 6 }}>
                  <button onClick={() => onSelect(order)} className='stop-button' >
                     <div className='stop-data'>
                        {/* Primera línea: Orden y plagas */}
                        <div className='stop-order-number'>
                           {order?.orderNumber && (
                              lang === 'en'
                                 ? `Order #${order.orderNumber}`
                                 : `Orden #${order.orderNumber}`
                           )}
                           <span 
                              style={{ 
                                 fontSize: 16, 
                                 fontWeight: 400, 
                                 color: 'tomato', 
                                 marginLeft: 8, 
                                 marginRight: 8,
                              }}
                           >
                              {order.treatment}
                           </span>
                           {Array.isArray(order.pests) && order.pests.length > 0 && (
                           <span style={{ fontSize: 16, fontWeight: 400, color: '#334155', marginLeft: 8 }}>
                              {order.pests.join(', ')}
                           </span>
                           )}
                        </div>
                        {/* Segunda línea: dirección + botón Google Maps */}
                        <div className='stop-address'>
                           <span>
                              {order.building?.address ||
                                    order.address ||
                                    (lang === 'en'
                                       ? 'No address'
                                       : 'Sin dirección')}
                           </span>
                        </div>
                        {/* Tercera línea: manager y teléfono */}
                        <div className='stop-manager'>
                           {order.building?.manager ||
                              order.manager ||
                              (lang === 'en'
                                 ? 'No manager'
                                 : 'Sin encargado')}
                           {' - '}
                           {order.building?.managerPhone ||
                              order.managerPhone ||
                              (lang === 'en'
                                 ? 'No phone'
                                 : 'Sin teléfono')}
                        </div>
                     </div>
                     {(order.building?.address || order.address) && (
                        <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.building?.address || order.address)}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           className='stop-map-link'
                           title={lang === 'en' ? 'Open in Google Maps' : 'Abrir en Google Maps'}>
                           <AddressPointIcon />
                        </a>
                     )}
                  </button>
               </li>
            ))}
         </ul>
      </div>
   )
}
